import React, { useEffect, useRef, useState } from 'react';
import Reveal from './Reveal';
import { PREVIEW_VIDEO_ID } from './courseContent';
import './courseKit.css';

/**
 * Free class preview. Udemy requires previewable lecture content because it is
 * one of the strongest enrollment levers, and it is the most direct way to
 * raise Hormozi's "perceived likelihood of achievement" term: people can see
 * the teaching before they buy.
 *
 * The player is mounted behind the poster as soon as the section is near the
 * viewport, and the poster tap sends YouTube a playVideo command. iOS only
 * honours autoplay when the iframe already exists at the moment of the user
 * gesture; mounting the iframe on tap made every iPhone visitor tap twice
 * (poster, then YouTube's own play button). Rob reported it on 2026-09-16.
 */
const PreviewVideo = ({
  videoId = PREVIEW_VIDEO_ID,
  heading = 'See what you build in the AI Trading Bootcamp',
  blurb = 'Watch a real Challenge lesson, then see how the five-week Bootcamp turns that workflow into your own AI trading agent.',
  videoTitle = 'Rob Grzesik demonstrates an AI trading workflow and the five-week Bootcamp',
  playLabel = 'Play the 18-minute AI Trading Bootcamp class preview',
  sectionLabel = 'AI Trading Bootcamp class preview',
  onPlay,
  onComplete,
}) => {
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef(null);
  const completedRef = useRef(false);

  const listenForPlayerEvents = () => {
    const playerWindow = iframeRef.current?.contentWindow;
    if (!playerWindow) return;
    const playerId = `bootcamp-preview-${videoId}`;
    playerWindow.postMessage(
      JSON.stringify({ event: 'listening', id: playerId }),
      'https://www.youtube-nocookie.com',
    );
    playerWindow.postMessage(
      JSON.stringify({
        event: 'command',
        func: 'addEventListener',
        args: ['onStateChange'],
        id: playerId,
      }),
      'https://www.youtube-nocookie.com',
    );
  };

  useEffect(() => {
    if (playing) iframeRef.current?.focus();
  }, [playing]);

  useEffect(() => {
    if (!playing || !onComplete) return undefined;

    const handlePlayerMessage = (event) => {
      if (
        event.origin !== 'https://www.youtube-nocookie.com' &&
        event.origin !== 'https://www.youtube.com'
      ) {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) return;

      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      const ended = payload?.event === 'onStateChange' && payload?.info === 0;
      const currentTime = Number(payload?.info?.currentTime);
      const duration = Number(payload?.info?.duration);
      const watchedToEnd = duration > 0 && currentTime / duration >= 0.95;
      if (!completedRef.current && (ended || watchedToEnd)) {
        completedRef.current = true;
        onComplete();
      }
    };

    window.addEventListener('message', handlePlayerMessage);
    return () => window.removeEventListener('message', handlePlayerMessage);
  }, [onComplete, playing]);

  const playerOrigin = 'https://www.youtube-nocookie.com';
  const startPlayback = () => {
    onPlay?.();
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
      playerOrigin,
    );
    setPlaying(true);
  };

  return (
    <section className="ck-preview" aria-label={sectionLabel}>
      <Reveal>
        <h2>{heading}</h2>
      </Reveal>
      <Reveal delay={70}>
        <p className="ck-preview-blurb">{blurb}</p>
      </Reveal>
      <Reveal delay={120}>
        <div className="ck-preview-frame">
          <iframe
            ref={iframeRef}
            src={`${playerOrigin}/embed/${videoId}?rel=0&modestbranding=1&enablejsapi=1&playsinline=1`}
            title={videoTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            tabIndex={playing ? 0 : -1}
            onLoad={listenForPlayerEvents}
          />
          {playing ? null : (
            <button
              type="button"
              className="ck-preview-poster"
              onClick={startPlayback}
              aria-label={playLabel}
              data-testid="preview-play"
            >
              <img
                src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
                alt=""
                loading="lazy"
                width="1280"
                height="720"
              />
              <span className="ck-preview-play" aria-hidden="true" />
            </button>
          )}
        </div>
      </Reveal>
    </section>
  );
};

export default PreviewVideo;
